// The single-candidate decision (`?decide=<aid>` on the Pipeline or the HM advance/hold/pass screen, DS §1.11, P-9):
// the Hiring Manager of the requisition decides one candidate of a slate with the same Advance · Hold · Pass control
// and reasons, through `candidate.decide_resume`. A Pass routes to the disposition worklist with its reason. Anyone
// else, and a candidate no longer Pending Review, gets no control.

import { useState } from "react";
import type { ApplicationCard, CandidateSlateOpenOutput } from "@/api/tools";
import { candidateDecideResume } from "@/api/tools";
import { useWrite } from "@/api/useTool";
import { ACTION_LABELS, HM_REVIEW_STATUS, KeyValue, Pill, Quote, RULE_LABELS, SLATE_STATUS, SlideOver, Stack, formatDate } from "@/mc";
import { CANDIDATE_LABELS } from "@/views/candidate/labels";
import { useHoldReasonChoices, usePassReasonChoices, useSourceWord } from "@/views/candidate/options";
import { DecisionControls, OriginalResume } from "./Decision";
import { EMPTY_DECISION, decisionBlock, decisionPayload, nameOf, type DecisionDraft } from "./pipeline";
import { pipelineRefusal } from "./refusals";

export function DecisionSlideOver({ rid, card, slate, onClose }: {
  rid: string; card: ApplicationCard | null; slate: CandidateSlateOpenOutput | null; onClose: () => void;
}) {
  const sourceWord = useSourceWord();
  const holdReasons = useHoldReasonChoices();
  const passReasons = usePassReasonChoices();
  const [draft, setDraft] = useState<DecisionDraft>(EMPTY_DECISION);
  const [refused, setRefused] = useState<string | null>(null);
  const decide = useWrite(
    (input: Parameters<typeof candidateDecideResume>[0], key) => candidateDecideResume(input, key),
    [["pipeline"], ["candidates", rid], ["slate", rid], ["application", card?.id ?? ""], ["today"]],
  );
  if (!card || card.stage !== "in_review") return null;
  const why = decisionBlock(draft);
  const submit = () => {
    setRefused(null);
    const { application_id, ...rest } = decisionPayload(card.id, draft);
    decide
      .mutateAsync({ requisition_id: rid, application_id, ...rest })
      .then(onClose)
      .catch((error: unknown) => setRefused(pipelineRefusal(error) ?? RULE_LABELS.failedSyncRetryable));
  };
  return (
    <SlideOver
      open
      title={nameOf(card)}
      onClose={onClose}
      refused={refused ? { reason: refused } : undefined}
      primary={{ label: ACTION_LABELS.submit, onClick: submit, busy: decide.isPending, blocked: why ? { reason: why } : undefined, hs: "pipe.decide.submit" }}
      
    >
      <Stack gap={12}>
        <Pill tone="info">{HM_REVIEW_STATUS.pendingReview}</Pill>
        <KeyValue
          cols={2}
          items={[
            [CANDIDATE_LABELS.source, sourceWord(card.source_channel)],
            ...(slate?.submitted_at ? [[SLATE_STATUS.submitted, formatDate(slate.submitted_at)] as const] : []),
          ]}
        />
        {card.recruiter_note ? <Quote>{card.recruiter_note}</Quote> : null}
        <OriginalResume rid={rid} card={card}  />
        <DecisionControls draft={draft} onChange={setDraft} holdReasons={holdReasons} passReasons={passReasons} scope="decide" busy={decide.isPending} />
      </Stack>
    </SlideOver>
  );
}
